import type { SkillView } from "@tutor/live";
import type { SkillProgress } from "@tutor/protocol";
import { Badge, Climb } from "@tutor/ui";
import { useEffect, useState } from "react";
import { useLocale, useT } from "../i18n";
import { skillLabel } from "./skill-labels";
import { stageWords } from "./stages";

// Progress cards: one climb per skill (ADR-0043). The step comes from the engine
// (`item.graded` in `view.progress`, ADR-0040) and the order from its mastery updates
// (`view.skills`); nothing is read from model text (CLAUDE.md rule 1). Children see stages,
// never a percentage: parents keep the percentages on their own cards.

export interface ClimbRow {
  readonly skillId: string;
  /** The engine's progress, or null for a skill practised before it reported steps. */
  readonly progress: SkillProgress | null;
}

/** Every skill the engine has reported, most recently practised first. */
export function climbRows(
  skills: Readonly<Record<string, SkillView>>,
  progress: Readonly<Record<string, SkillProgress>> = {},
): ClimbRow[] {
  const ids = new Set([...Object.keys(skills), ...Object.keys(progress)]);
  const at = (id: string) => skills[id]?.at ?? "";
  return [...ids]
    .sort((a, b) => (at(a) < at(b) ? 1 : at(a) > at(b) ? -1 : a.localeCompare(b)))
    .map((skillId) => ({ skillId, progress: progress[skillId] ?? null }));
}

function atSummit(row: ClimbRow): boolean {
  return row.progress !== null && row.progress.step >= row.progress.steps;
}

// The step each skill showed on this device last time, so a stage reached since then gets
// a badge. Skill ids and step numbers only; no personal data.
const SEEN_KEY = "tutor.climb.seen";

function loadSeen(): Readonly<Record<string, number>> {
  try {
    const parsed: unknown = JSON.parse(window.localStorage.getItem(SEEN_KEY) ?? "{}");
    if (typeof parsed !== "object" || parsed === null) return {};
    return Object.fromEntries(
      Object.entries(parsed).filter((e): e is [string, number] => typeof e[1] === "number"),
    );
  } catch {
    return {};
  }
}

function saveSeen(rows: readonly ClimbRow[]): void {
  try {
    const seen: Record<string, number> = { ...loadSeen() };
    for (const row of rows) if (row.progress !== null) seen[row.skillId] = row.progress.step;
    window.localStorage.setItem(SEEN_KEY, JSON.stringify(seen));
  } catch {
    // Private mode or storage off: no badge next time, nothing else changes.
  }
}

export function InsightCards(props: {
  skills: Readonly<Record<string, SkillView>>;
  progress?: Readonly<Record<string, SkillProgress>>;
  compact?: boolean;
}) {
  const t = useT();
  const locale = useLocale();
  const rows = climbRows(props.skills, props.progress);
  // Read once per mount: the badge stays while the child looks at the screen.
  const [seen] = useState(loadSeen);
  useEffect(() => {
    saveSeen(rows);
  });
  const climbing = rows.filter((row) => !atSummit(row));
  const summits = rows.filter(atSummit);
  return (
    <section className={props.compact === true ? "insights compact" : "insights"}>
      <h2 className="insights-title">{t("insights.title")}</h2>
      {rows.length === 0 ? (
        <p className="insights-empty">{t("insights.empty")}</p>
      ) : (
        <>
          {climbing.length > 0 && (
            <ul className="insight-list">
              {climbing.map((row) => {
                const label = skillLabel(row.skillId, locale);
                if (row.progress === null) {
                  return (
                    <li key={row.skillId} className="insight" data-skill-id={row.skillId}>
                      <span className="insight-label">{label}</span>
                      <span className="insight-where">{t("insights.started")}</span>
                    </li>
                  );
                }
                const { step, steps } = row.progress;
                const words = stageWords(t, label, step, steps);
                const before = seen[row.skillId];
                const fresh = before !== undefined && step > before;
                return (
                  <li key={row.skillId} className="insight" data-skill-id={row.skillId}>
                    <span className="insight-label">
                      {label}
                      {fresh && <Badge tone="sun">{t("insights.new")}</Badge>}
                    </span>
                    <Climb
                      step={step}
                      steps={steps}
                      size="card"
                      label={words.label}
                      fresh={fresh}
                    />
                    <span className="insight-where" aria-hidden="true">
                      {words.where}
                    </span>
                    <span className="insight-next">{words.next}</span>
                  </li>
                );
              })}
            </ul>
          )}
          {summits.length > 0 && (
            <>
              <h3 className="insights-summits-title">{t("insights.summits")}</h3>
              <ul className="insight-summits">
                {summits.map((row) => (
                  <li key={row.skillId} className="insight-summit" data-skill-id={row.skillId}>
                    <svg width="14" height="16" viewBox="0 0 14 16" aria-hidden="true">
                      <line x1="2" y1="1" x2="2" y2="15" />
                      <path d="M2 1 l10 4 l-10 4 z" />
                    </svg>
                    {skillLabel(row.skillId, locale)}
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      )}
    </section>
  );
}
